import { useMedia, useStayUnsynced } from "@/hooks/contextHooks"
import { Box, Center, Flex, Heading, IconButton, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, Text, Tooltip, useColorModeValue, Button, Badge, HStack, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, Portal, VStack } from "@chakra-ui/react"
import { faArrowLeft, faArrowRight, faCompress, faExpand, faPause, faPlay, faMobileAlt, faTv, faCropSimple, faScissors, faVideo } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MediaMeta, PixelCoordinate } from "@xnav/types/src/common"
import { useEffect, useRef, useState, useContext } from "react"
import { MarkupTool } from "./videoMarkupEditor"
import { EditContent } from "@/pages/editor"
import { drawContents, getClientMousePosition, handleCanvasClick, handleCanvasDrag, scaleMarkup } from "@/functions/canvasFunctions"
import MarkupToolbar from "./markupToolbar"
import { NavCollapsedContext } from "@/contexts/contexts"

const VideoMarkupPlayer: React.FC<{
	content: EditContent
	videoPaused: boolean,
	videoTime: number,
	videoDuration: number,
	fullScreen: boolean,
	tool: MarkupTool,
	setVideoPaused: React.Dispatch<React.SetStateAction<boolean>>,
	setVideoTime: React.Dispatch<React.SetStateAction<number>>,
	setVideoDuration: React.Dispatch<React.SetStateAction<number>>
	setFullScreen: React.Dispatch<React.SetStateAction<boolean>>
	setContent: (content: EditContent) => void,
	setTool: React.Dispatch<React.SetStateAction<MarkupTool>>,
	isExpanded?: boolean,
	isRightPanelCollapsed?: boolean,
	isTrimming?: boolean,
	setIsTrimming?: (val: boolean) => void,
	onExpandRightPanel?: () => void,
	onSelectNewVideo?: () => void
}> = ({ content, videoPaused, videoTime, videoDuration, fullScreen, tool, setVideoPaused, setVideoTime, setVideoDuration, setFullScreen, setContent, setTool, isExpanded, isRightPanelCollapsed, isTrimming, setIsTrimming, onExpandRightPanel, onSelectNewVideo }) => {
	const [video, setVideo] = useState<MediaMeta>()
	const [media,] = useMedia()
	const [isNavCollapsed] = useContext(NavCollapsedContext)

	const videoRef = useRef<HTMLVideoElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>()
	const contextRef = useRef<CanvasRenderingContext2D>()

	const mouseDown = useRef(false)
	const isDragging = useRef(false)
	const lastMousePosition = useRef<PixelCoordinate>({ x: 0, y: 0 })

	const containerRef = useRef<HTMLDivElement>(null)
	const [canvasDimensions, setCanvasDimensions] = useState<{ width: number, height: number }>({ width: 600, height: 400 })
	const [aspectMode, setAspectMode] = useState<'auto' | 'portrait' | 'landscape'>('auto')
	const [isPortraitActive, setIsPortraitActive] = useState<boolean>(false)

	function shrinkToAspect(box: PixelCoordinate, aspect: PixelCoordinate): PixelCoordinate {
		if (box.x < 50) box.x = 50
		if (box.y < 50) box.y = 50
		if (box.x / box.y > aspect.x / aspect.y) return { x: Math.round((aspect.x / aspect.y) * box.y), y: Math.round(box.y) }
		return { x: Math.round(box.x), y: Math.round((aspect.y / aspect.x) * box.x) }
	}

	const detectBlackPillarbox = (): boolean => {
		const vid = videoRef.current
		if (!vid || !vid.videoWidth || !vid.videoHeight) return false
		try {
			const offCanvas = document.createElement('canvas')
			offCanvas.width = 100
			offCanvas.height = 100
			const ctx = offCanvas.getContext('2d')
			if (!ctx) return false
			ctx.drawImage(vid, 0, 0, 100, 100)
			const leftData = ctx.getImageData(5, 50, 1, 1).data
			const rightData = ctx.getImageData(95, 50, 1, 1).data
			const centerData = ctx.getImageData(50, 50, 1, 1).data

			const isLeftBlack = leftData[0] < 25 && leftData[1] < 25 && leftData[2] < 25
			const isRightBlack = rightData[0] < 25 && rightData[1] < 25 && rightData[2] < 25
			const isCenterNonBlack = centerData[0] > 20 || centerData[1] > 20 || centerData[2] > 20

			return isLeftBlack && isRightBlack && isCenterNonBlack
		} catch (e) {
			return false
		}
	}

	function setCanvasSize() {
		if (!videoRef.current || !canvasRef.current) return
		const vid = videoRef.current
		const vWidth = vid.videoWidth
		const vHeight = vid.videoHeight

		let isPortrait = false
		if (aspectMode === 'portrait') {
			isPortrait = true
		} else if (aspectMode === 'landscape') {
			isPortrait = false
		} else {
			if (vHeight > vWidth) {
				isPortrait = true
			} else if (vWidth && vHeight && (vWidth / vHeight > 1.2)) {
				isPortrait = detectBlackPillarbox()
			}
		}

		setIsPortraitActive(isPortrait)

		const vidAspect = isPortrait ? { x: 9, y: 16 } : ((vWidth && vHeight) ? { x: vWidth, y: vHeight } : { x: 16, y: 9 })

		const container = containerRef.current || vid.parentElement
		const boxWidth = container ? container.clientWidth : 600
		const boxHeight = container ? container.clientHeight : 400

		if (!boxWidth || !boxHeight) return

		const { x: width, y: height } = shrinkToAspect({ x: boxWidth, y: boxHeight }, vidAspect)

		if (width > 0 && height > 0) {
			canvasRef.current.width = width
			canvasRef.current.height = height
			setCanvasDimensions({ width, height })

			if (vWidth && vWidth > 0) {
				const newClientScale = vWidth / width
				if (newClientScale && isFinite(newClientScale) && newClientScale > 0) {
					setContent(prev => {
						if (Math.abs((prev.clientScale ?? 1) - newClientScale) < 0.002) return prev;
						return {
							...scaleMarkup((prev.clientScale ?? 1) / newClientScale, prev),
							clientScale: newClientScale
						};
					});
				}
			}
		}
	}

	function handleCanvasRef(newCanvasRef: HTMLCanvasElement) {
		if (!newCanvasRef) return
		const newContextRef = newCanvasRef.getContext('2d')!
		canvasRef.current = newCanvasRef
		contextRef.current = newContextRef
	}

	const hasValidClip = Boolean(
		content?.clipStart !== undefined &&
		content?.clipEnd !== undefined &&
		content.clipEnd > content.clipStart
	);
	const clipStart = hasValidClip ? content.clipStart! : 0;
	const clipEnd = hasValidClip ? content.clipEnd! : (videoDuration || 0);
	const effectiveDuration = Math.max(0.1, clipEnd - clipStart);

	function handleVideoMetaLoad() {
		const vidRef = videoRef.current
		if (!vidRef) return
		const dur = vidRef.duration || 0;
		setVideoDuration(dur);
		const isValid = content?.clipStart !== undefined && content?.clipEnd !== undefined && content.clipEnd > content.clipStart;
		const startTime = isValid ? content.clipStart! : 0;
		vidRef.currentTime = startTime;
		setVideoTime(startTime)
		setCanvasSize()
	}

	function draw() {
		const time = Math.round(videoRef.current!.currentTime * 10) / 10
		drawContents(contextRef.current!, content, time)
		if (videoRef.current && !videoRef.current.paused) {
			requestAnimationFrame(draw)
		}
	}

	useEffect(() => {
		if (media && content) {
			const found = media.find((m: any) => 
				(content.videoId && m.id === content.videoId) || 
				(content.videoTitle && m.name === content.videoTitle) || 
				(content.id && m.references?.includes(content.id)) ||
				(content.title && m.name === content.title)
			);
			if (found && found !== video) {
				setVideo(found);
			}
		}
	}, [media, content?.videoId, content?.videoTitle, content?.id, content?.title, video])

	useEffect(() => {
		setCanvasSize()
	}, [fullScreen, isExpanded, isRightPanelCollapsed, aspectMode, isNavCollapsed])

	useEffect(() => {
		const container = containerRef.current
		if (!container) return
		let rafId: number | null = null;
		const resizeObserver = new ResizeObserver(() => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				setCanvasSize();
			});
		})
		resizeObserver.observe(container)
		window.addEventListener('resize', setCanvasSize)
		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			resizeObserver.disconnect()
			window.removeEventListener('resize', setCanvasSize)
		}
	}, [])

	useEffect(() => {
		if (videoDuration > 0 && videoTime >= (hasValidClip ? clipEnd : videoDuration)) {
			setVideoPaused(true);
		}
		if (contextRef.current && videoPaused) drawContents(contextRef.current, content, Math.round(videoTime * 10) / 10)
	}, [content, videoTime, videoDuration, hasValidClip, clipEnd, videoPaused])

	useEffect(() => {
		const vid = videoRef.current;
		if (!vid) return;

		if (videoPaused) {
			vid.pause();
		} else {
			if (hasValidClip) {
				const ct = vid.currentTime;
				if (ct < clipStart || ct >= clipEnd - 0.05) {
					vid.currentTime = clipStart;
					setVideoTime(clipStart);
				}
			} else {
				const maxDur = videoDuration || vid.duration || 0;
				if (maxDur > 0 && vid.currentTime >= maxDur - 0.05) {
					vid.currentTime = 0;
					setVideoTime(0);
				}
			}

			const playPromise = vid.play();
			if (playPromise !== undefined) {
				playPromise.catch((err) => {
					console.warn('Playback error or aborted:', err);
					setVideoPaused(true);
				});
			}
			draw();
		}
	}, [videoPaused, hasValidClip, clipStart, clipEnd, videoDuration])

	useEffect(() => {
		const vid = videoRef.current;
		if (!vid) return;
		if (Math.abs(vid.currentTime - videoTime) > 0.08) {
			vid.currentTime = videoTime;
		}
	}, [videoTime])

	useEffect(() => {
		const vid = videoRef.current;
		if (!vid) return;
		if (hasValidClip) {
			if (videoTime < clipStart || videoTime >= clipEnd) {
				vid.currentTime = clipStart;
				setVideoTime(clipStart);
			}
		}
	}, [content?.id, hasValidClip, clipStart, clipEnd])

	useEffect(() => {
		if (mouseDown.current) isDragging.current = true
	}, [content])

	if (video) return (
		<Flex flexDir='column' w='100%' h='100%' flex={1} minH={0} pos='relative'>
			<Box 
				ref={containerRef} 
				bg='transparent' 
				flex={1} 
				w='100%' 
				h='100%'
				minH={0}
				pos='relative' 
				borderRadius='xl' 
				overflow='hidden' 
				display="flex" 
				alignItems="center" 
				justifyContent="center" 
				p={1}
			>
				<Box pos="relative" w={`${canvasDimensions.width}px`} h={`${canvasDimensions.height}px`} boxShadow="0 8px 30px rgba(0,0,0,0.12)" borderRadius="xl" overflow="hidden">
					<video
						src={video.fileUrl}
						ref={videoRef}
						playsInline
						preload="auto"
						style={{
							position: 'absolute',
							left: 0,
							right: 0,
							top: 0,
							bottom: 0,
							width: '100%',
							height: '100%',
							objectFit: isPortraitActive ? 'cover' : 'contain',
						}}
						onLoadedMetadata={handleVideoMetaLoad}
						onLoadedData={setCanvasSize}
						onCanPlay={setCanvasSize}
						onTimeUpdate={(e) => {
							const vid = e.currentTarget;
							const ct = vid.currentTime;
							if (hasValidClip) {
								if (ct < clipStart) {
									vid.currentTime = clipStart;
									setVideoTime(clipStart);
									return;
								}
								if (ct >= clipEnd) {
									vid.currentTime = clipStart;
									setVideoTime(clipStart);
									setVideoPaused(true);
									return;
								}
							}
							if (!videoPaused) {
								setVideoTime(ct);
							}
						}}
						onEnded={() => {
							setVideoPaused(true);
							if (hasValidClip) {
								if (videoRef.current) videoRef.current.currentTime = clipStart;
								setVideoTime(clipStart);
							}
						}}
					/>
					<canvas
						ref={handleCanvasRef}
						onMouseDown={(e) => {
							handleCanvasClick(e.nativeEvent, content, setContent, tool, Math.round(videoTime * 10) / 10, setTool)
							mouseDown.current = true
							lastMousePosition.current = getClientMousePosition(e.nativeEvent)
						}}
						onMouseUp={() => {
							isDragging.current = false
							mouseDown.current = false
						}}
						onMouseMove={(e) => {
							if (isDragging.current) handleCanvasDrag(e.nativeEvent, lastMousePosition.current, content, setContent)
							lastMousePosition.current = getClientMousePosition(e.nativeEvent)
						}}
						style={{
							position: 'absolute',
							left: 0,
							right: 0,
							top: 0,
							bottom: 0,
							width: '100%',
							height: '100%',
							zIndex: 10,
						}}
					/>
				</Box>
			</Box>
			{/* The video controls bar */}
			<Box w="100%" mt={1} mb={0.5} zIndex={20}>
				<Flex
					bg={useColorModeValue('white', 'gray.800')}
					border="1px solid"
					borderColor={useColorModeValue('gray.200', 'gray.700')}
					borderRadius="full"
					boxShadow="sm"
					py={1.5}
					px={5}
					gap={4}
					alignItems="center"
					w="full"
					maxW="850px"
					mx="auto"
				>
					<Flex gap={2}>
						{videoPaused ? (
							<Tooltip label="Play" placement="top" hasArrow openDelay={300}>
								<IconButton
									variant='ghost'
									colorScheme="blue"
									borderRadius="full"
									aria-label="Play Video"
									icon={<FontAwesomeIcon icon={faPlay} />}
									onClick={() => {
										if (videoRef.current) {
											if (hasValidClip) {
												const ct = videoRef.current.currentTime;
												if (ct < clipStart || ct >= clipEnd - 0.05 || videoTime < clipStart || videoTime >= clipEnd - 0.05) {
													videoRef.current.currentTime = clipStart;
													setVideoTime(clipStart);
												}
											} else if (videoRef.current.currentTime >= (videoDuration || 1) - 0.05 || videoTime >= (videoDuration || 1) - 0.05) {
												videoRef.current.currentTime = 0;
												setVideoTime(0);
											}
										}
										setVideoPaused(false);
									}}
									_hover={{ transform: 'scale(1.1)', bg: 'blue.50' }}
									_dark={{ _hover: { bg: 'blue.900' } }}
									transition="all 0.2s"
								/>
							</Tooltip>
						) : (
							<Tooltip label="Pause" placement="top" hasArrow openDelay={300}>
								<IconButton
									variant='ghost'
									colorScheme="blue"
									borderRadius="full"
									aria-label="Pause Video"
									icon={<FontAwesomeIcon icon={faPause} />}
									onClick={() => setVideoPaused(true)}
									_hover={{ transform: 'scale(1.1)', bg: 'blue.50' }}
									_dark={{ _hover: { bg: 'blue.900' } }}
									transition="all 0.2s"
								/>
							</Tooltip>
						)}
						<Tooltip label="Rewind 0.1s" placement="top" hasArrow openDelay={300}>
							<IconButton variant='ghost' borderRadius="full" aria-label="Skip back .1s" icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => setVideoTime(Math.max(clipStart, Math.round((videoTime - .1) * 10) / 10))} _hover={{ transform: 'scale(1.1)' }} transition="all 0.2s" />
						</Tooltip>
						<Tooltip label="Forward 0.1s" placement="top" hasArrow openDelay={300}>
							<IconButton variant='ghost' borderRadius="full" aria-label="Skip forward .1s" icon={<FontAwesomeIcon icon={faArrowRight} />} onClick={() => setVideoTime(Math.min(clipEnd, Math.round((videoTime + .1) * 10) / 10))} _hover={{ transform: 'scale(1.1)' }} transition="all 0.2s" />
						</Tooltip>
					</Flex>
					<HStack spacing={1.5} minW="100px" justify="center">
						<Tooltip label={hasValidClip ? `Master Video Time: ${videoTime.toFixed(1)}s / ${videoDuration.toFixed(1)}s` : undefined} placement="top" hasArrow>
							<Text textAlign='center' fontSize="sm" fontWeight="600" opacity={.8} cursor="default">
								{hasValidClip ? `${(videoTime - clipStart).toFixed(1)}s` : `${videoTime.toFixed(1)}s`}{' '}
								<Text as="span" opacity={0.5}>/ {effectiveDuration.toFixed(1)}s</Text>
							</Text>
						</Tooltip>
						<Tooltip label={hasValidClip ? `Active Clip: ${effectiveDuration.toFixed(1)}s (Master: ${videoDuration.toFixed(1)}s)` : "Trim this chapter from master video"} placement="top" hasArrow>
							<Button
								size="2xs"
								variant={isTrimming ? "solid" : (hasValidClip ? "solid" : "outline")}
								colorScheme="teal"
								leftIcon={<FontAwesomeIcon icon={faScissors} />}
								borderRadius="full"
								px={2.5}
								py={1}
								fontSize="10px"
								fontWeight="bold"
								onClick={() => {
									setIsTrimming?.(!isTrimming);
									if (onExpandRightPanel) onExpandRightPanel();
								}}
							>
								{hasValidClip ? `Trimmed (${effectiveDuration.toFixed(1)}s)` : "Trim Chapter"}
							</Button>
						</Tooltip>
					</HStack>
					<Slider
						flex={1}
						min={clipStart}
						max={clipEnd}
						value={Math.max(clipStart, Math.min(clipEnd, videoTime))}
						step={.1}
						onChange={(e) => {
							setVideoTime(e);
							if (videoRef.current) {
								videoRef.current.currentTime = e;
							}
						}}
						focusThumbOnChange={false}
					>
						<SliderTrack bg="gray.200" _dark={{ bg: "whiteAlpha.200" }} h="6px" borderRadius="full">
							<SliderFilledTrack bgGradient="linear(to-r, blue.400, purple.500)" />
						</SliderTrack>
						<SliderThumb boxSize={4} bg="white" boxShadow="md" border="2px solid" borderColor="purple.500" _focus={{ boxShadow: "0 0 0 3px rgba(128, 90, 213, 0.4)" }} transition="transform 0.1s" _hover={{ transform: 'scale(1.2)' }} />
					</Slider>
					
					{/* Aspect Ratio Mode Switcher */}
					<Tooltip label={`Mode: ${aspectMode.toUpperCase()} (Click to toggle Portrait / Landscape / Auto)`} placement="top" hasArrow openDelay={300}>
						<Button
							size="xs"
							variant="ghost"
							borderRadius="full"
							colorScheme={isPortraitActive ? "purple" : "gray"}
							leftIcon={<FontAwesomeIcon icon={isPortraitActive ? faMobileAlt : faTv} />}
							onClick={() => {
								if (aspectMode === 'auto') setAspectMode('portrait')
								else if (aspectMode === 'portrait') setAspectMode('landscape')
								else setAspectMode('auto')
							}}
						>
							{aspectMode === 'portrait' ? 'Portrait' : aspectMode === 'landscape' ? 'Landscape' : (isPortraitActive ? 'Portrait (Auto)' : 'Fit')}
						</Button>
					</Tooltip>

					{/* Change / Replace Video button */}
					{onSelectNewVideo && (
						<Tooltip label="Choose or replace the video file" placement="top" hasArrow openDelay={300}>
							<Button
								size="xs"
								variant="ghost"
								borderRadius="full"
								colorScheme="blue"
								leftIcon={<FontAwesomeIcon icon={faVideo} />}
								onClick={onSelectNewVideo}
							>
								Change Video
							</Button>
						</Tooltip>
					)}

					<Tooltip label={fullScreen ? "Exit Full Screen" : "Enter Full Screen"} placement="top" hasArrow openDelay={300}>
						<IconButton
							borderRadius="full"
							aria-label={fullScreen ? "Exit Full Screen" : "Enter Full Screen"}
							icon={<FontAwesomeIcon icon={fullScreen ? faCompress : faExpand} />}
							variant='ghost'
							onClick={() => setFullScreen(!fullScreen)}
							_hover={{ transform: 'scale(1.1)' }}
							transition="all 0.2s"
						/>
					</Tooltip>
				</Flex>
			</Box>
		</Flex>
	)
	return (
		<Center flexDir='column' w='full' h='full' gap={5}>
			<Spinner />
			<Heading>Loading Video...</Heading>
		</Center>
	)
}

export default VideoMarkupPlayer
